import styled, { css } from 'styled-components';

import {
  black,
  border1,
  boxShadow1,
  exactWhite,
  ocean,
  gray,
} from 'utils/style/colors';
import { placeholderAnimate } from 'utils/style/animations';
import { convertNumbersToProperFormat } from 'utils/number';

interface IInputGroupProps {
  hide: boolean;
  count: number;
}

interface IPeriodLabelProps {
  isActive: boolean;
}

interface IPeriodTypeContainerProps {
  isActive: boolean;
}

const placeholderAnimateRule = css`
  ${placeholderAnimate} 1.7s linear forwards infinite;
`;

export const Container = styled.div`
  width: 100%;
  margin: 24px 0;
  padding: 6px;
  display: flex;
  overflow: auto;
  flex-flow: column;
  box-shadow: ${boxShadow1};
  border-radius: 10px;
  justify-content: space-around;
  background-color: ${gray};
`;

export const Header = styled.div`
  color: ${gray};
  width: 100%;
  padding: 13px;
  text-align: center;
  font-weight: 500;
  border-radius: 10px 10px 0 0;
  border-bottom: ${border1};
  background-color: ${ocean};
`;

export const PeriodLabel = styled.label<IPeriodLabelProps>`
  color: ${(props) => (props.isActive ? black : gray)} !important;
  font-weight: 500 !important;

  &:before {
    visibility: hidden !important;
  }
`;

export const Periods = styled('div')<IInputGroupProps>`
  display: flex;
  position: relative;
  transition: all 0.1s ease-in;
  align-items: ${(props) => (props.hide ? 'center' : 'inherit')} !important;

  &:hover {
    background-color: #dcdcdc;
  }

  & > ${PeriodLabel} {
    width: 100%;
    height: inherit;
    z-index: 2;
    display: flex !important;
    padding: 0.25rem 0 !important;
    overflow: hidden;
    position: relative !important;
    font-size: 13px !important;
    font-weight: 600;
    text-align: center !important;
    transition: color 0.2s ease-in !important;
    flex-direction: column;
    justify-content: ${(props) =>
      props.hide ? 'center' : 'flex-start'} !important;

    &:before {
      margin: 0;
      opacity: 0;
      content: '';
      z-index: -1 !important;
      position: absolute;
      transform: translate(50%, -50%) scale3d(1, 1, 1);
      transition: color 100ms ease-out !important;
    }

    &:after {
      top: 70% !important;
      min-width: 27px !important;
      color: ${black};
      right: inherit !important;
      height: 25px !important;
      cursor: pointer !important;
      margin: 0 !important;
      position: absolute !important;
      font-size: 14px !important;
      transform: translateY(-50%) translateX(-50%) !important;
      -webkit-transform: translateY(-50%) translateX(-50%) !important;
      align-self: center;
      text-align: center !important;
      transition: color 100ms ease-in !important;
      align-items: center;
      font-weight: 250 !important;
      border-radius: 10rem !important;
      justify-content: center;
      content: ${(props) =>
        `'${convertNumbersToProperFormat(props.count)}'`} !important;
      display: ${(props) => (props.hide ? 'none' : 'table')} !important;
      padding: 0 3px;
      left: 50% !important;
    }

    & > div {
      width: 50%;
      margin: 0 auto;
      height: 1px;
      background: ${black};
      padding: 0 3px;
    }
  }

  & > input {
    top: 50%;
    left: 6px;
    order: 1;
    width: 26px;
    height: 26px;
    cursor: pointer;
    z-index: 2;
    position: absolute;
    visibility: hidden;
    transform: translateY(-50%);
    -webkit-transform: translateY(-50%);

    &:checked ~ ${PeriodLabel} {
      color: var(--exact-white) !important;
      background-color: ${black} !important;
      border-radius: 0.5rem !important;
      &:before {
        opacity: 1;
        transform: translate(50%, -50%) scale3d(56, 56, 1);
        -webkit-transform: translate(50%, -50%) scale3d(56, 56, 1);
      }

      &:after {
        color: var(--exact-white);
        border-radius: 0 !important;
      }

      & > div {
        background-color: ${exactWhite} !important;
      }
    }
  }
`;

export const RangeHeader = styled.div`
  display: flex;
  padding: 12px;
  height: 2.5rem;
  transition: all 0.1s ease;
  align-items: center;
  font-weight: 500;
  justify-content: space-between;
  &:first-child,
  &:last-child {
    font-size: 13px;
    font-weight: 500;
  }
`;

export const PeriodTypeContainer = styled.div<IPeriodTypeContainerProps>`
  display: flex;
  overflow: auto;
  flex-direction: column;
`;

export const PeriodLoading = styled.div`
  width: 100%;
  height: 7.15em;
  overflow: hidden;
  position: relative;
  background: #f6f7f8;
  background: linear-gradient(to right, ${gray} 2%, #ddd 18%, ${gray} 33%);
  box-shadow: ${boxShadow1};
  border-radius: 10px;
  animation: ${placeholderAnimateRule};
  background-size: 1300px;
`;

export const Wrapper = styled.div`
  display: flex;
  overflow: hidden;
  flex-wrap: wrap;
  & > ${Periods} {
    flex: 1;
    height: 4.5rem;
    width: 4rem;
    border: ${border1};
    margin: 0.4rem;
    border-radius: 0.5rem;
    @media only screen and (max-width: 768px) {
      flex: none;
      width: calc(100% / 4);
    }
  }
  @media only screen and (max-width: 768px) {
    justify-content: center;
  }
`;

export const ChangeDate = styled.span`
  color: ${black};
  cursor: pointer;
  display: inline-flex;
  padding: 0.1rem 0.7rem;
  position: relative;
  align-items: center;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  border-radius: 0.5rem;
  &:hover {
    color: ${exactWhite};
    background-color: ${black};
  }
  & > i {
    font-size: 18px !important;
    margin-right: 0;
  }

  &:hover > i {
    color: ${exactWhite} !important;
  }
`;
